import type { Visitor, namedTypes } from 'ast-types';
import { NodePath } from 'ast-types/lib/node-path';
import type { Position } from 'monaco-editor';
import { parse as recastParse, visit as recastVisit } from 'recast';

import { getLastFromMinLengthArray } from 'src/lib/getLastFromMinLengthArray';

function positionIsWithinLoc(
  loc?: namedTypes.SourceLocation | null,
  position?: Position,
) {
  if (!loc?.start || !loc.end || !position) return false;
  return (
    (loc.start.line < position.lineNumber ||
      (loc.start.line === position.lineNumber &&
        loc.start.column <= position.column)) &&
    (loc.end.line > position.lineNumber ||
      (loc.end.line === position.lineNumber &&
        loc.end.column >= position.column))
  );
}

function isKey<Key extends string>(key: unknown, keys: Key[]): key is Key {
  return keys.includes(key as Key);
}
function isValue(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

type Scope = Array<
  { key: string } & (
    | { isVariable?: false; isMutable?: false; value: string | null }
    | { isVariable?: false; isMutable: true }
    | { isVariable: true; isMutable?: false; value: string }
  )
>;
function getVariableDetectionHelpers() {
  const variableScopes: MinLengthArray<1, Scope> = [[]];
  const visitors: Visitor<{}> = {
    visitVariableDeclaration(variablePath) {
      getLastFromMinLengthArray(variableScopes).push(
        ...variablePath.node.declarations.flatMap((declarator) => {
          if (
            declarator.type !== 'VariableDeclarator' ||
            declarator.id.type !== 'Identifier' ||
            !declarator.init
          ) {
            return [];
          } else if (variablePath.node.kind !== 'const') {
            return { key: declarator.id.name, isMutable: true as const };
          }

          return {
            key: declarator.id.name,
            ...(declarator.init.type === 'Literal' &&
            isValue(declarator.init.value)
              ? { value: declarator.init.value }
              : declarator.init.type === 'Identifier'
                ? { value: declarator.init.name, isVariable: true as const }
                : { isMutable: true as const }),
          };
        }),
      );

      return this.traverse(variablePath);
    },
    visitFunctionDeclaration(functionPath) {
      variableScopes.push(
        functionPath.node.params
          .filter(
            (param): param is Extract<typeof param, { type: 'Identifier' }> =>
              param.type === 'Identifier',
          )
          .map((param) => ({ key: param.name, isMutable: true })),
      );
      this.traverse(functionPath);
      variableScopes.pop();
    },
  };
  function getValueFromPropertyValue(
    variableName: string,
    scopes: Scope[] = variableScopes.slice().reverse(),
  ): string | null | undefined {
    for (let i = 0; i < scopes.length; i += 1) {
      for (const variable of scopes[i] ?? []) {
        if (variable.key === variableName) {
          if (variable.isVariable) {
            return getValueFromPropertyValue(variable.value, scopes.slice(i));
          } else if (variable.isMutable) {
            return undefined;
          } else {
            return variable.value;
          }
        }
      }
    }
    return undefined;
  }

  return {
    visitors,
    getValueFromPropertyValue(
      propertyPath: NodePath<namedTypes.Property, unknown>,
    ) {
      const propertyValue = propertyPath.node.value;
      if (propertyValue?.type === 'Identifier') {
        return getValueFromPropertyValue(propertyValue.name);
      } else if (
        propertyValue.type === 'Literal' &&
        isValue(propertyValue.value)
      ) {
        return propertyValue.value;
      }
    },
  };
}
export function getObjectValuesInDocument<Key extends string>(
  document: string,
  keys: Key[],
) {
  const usages = Object.fromEntries<(string | null)[]>(
    keys.map((key) => [`${key}s`, []]),
  ) as Record<`${Key}s`, (string | null)[]>;
  const variableHelpers = getVariableDetectionHelpers();
  recastVisit(recastParse(document, { tolerant: true }), {
    visitProperty(propertyPath: NodePath<namedTypes.Property, unknown>) {
      const value = variableHelpers.getValueFromPropertyValue(propertyPath);
      const key =
        propertyPath.node.key.type === 'Identifier' &&
        propertyPath.node.key.name;

      if (value !== undefined && isKey(key, keys)) {
        if (!usages[`${key}s`].includes(value)) {
          usages[`${key}s`].push(value);
        }
      }
      return this.traverse(propertyPath);
    },
    ...variableHelpers.visitors,
  });

  return { usages };
}

export function getObjectValuesAtPosition<Key extends string>(
  document: string,
  keys: Key[],
  position: Position,
) {
  let cursorKey: Key | undefined;
  const values: { [key in Key]?: string | null } = {};
  const variableHelpers = getVariableDetectionHelpers();
  try {
    recastVisit(recastParse(document, { tolerant: true }), {
      visitProperty(propertyPath: NodePath<namedTypes.Property, unknown>) {
        const value = variableHelpers.getValueFromPropertyValue(propertyPath);
        const key =
          propertyPath.node.key.type === 'Identifier' &&
          propertyPath.node.key.name;
        const objectLocation: namedTypes.SourceLocation | null | undefined =
          propertyPath.parentPath?.parentPath?.value?.loc;

        if (isKey(key, keys) && positionIsWithinLoc(objectLocation, position)) {
          if (value !== undefined) {
            values[key] = value;
          }
          if (positionIsWithinLoc(propertyPath.node.loc, position)) {
            cursorKey = key;
          }
        }
        return this.traverse(propertyPath);
      },
      ...variableHelpers.visitors,
    });
  } catch {
    // ignore errors
  }

  return { values, cursorKey };
}
